import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { formatINR, formatDate, todayISO } from "@/lib/format";
import { printSection } from "@/lib/print";
import {
  Boxes, TrendingDown, TrendingUp, Wallet, Landmark, AlertTriangle, CheckCircle2,
  Printer, ArrowDownRight, PackagePlus, Truck, X,
} from "lucide-react";

const ranges = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 Days" },
  { key: "mtd", label: "MTD (This Month)" },
  { key: "custom", label: "Custom Date" },
];

export default function Audit() {
  const [val, setVal] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [modal, setModal] = useState(null); // 'payables' | 'receivables'
  const [range, setRange] = useState("mtd");
  const [start, setStart] = useState(todayISO().slice(0, 8) + "01");
  const [end, setEnd] = useState(todayISO());
  const [report, setReport] = useState(null);

  useEffect(() => {
    api.get("/audit/valuation").then((r) => setVal(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  const loadReport = useCallback(() => {
    const params = { range };
    if (range === "custom") {
      params.start = start;
      params.end = end;
    }
    api.get("/audit/report", { params }).then((r) => setReport(r.data)).catch(() => {});
  }, [range, start, end]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  if (!val) return <div className="text-slate-400 font-semibold">Loading…</div>;

  const payableItems = vehicles
    .map((v) => ({
      id: v.id,
      party: v.party_name || "—",
      channel: v.channel,
      model: v.model,
      reg_no: v.reg_no,
      held: Math.max((v.total_buying_cost || 0) - (v.mediator_paid || 0), 0),
    }))
    .filter((x) => x.held > 0);

  const receivableItems = vehicles
    .filter((v) => ["booked", "sold"].includes(v.status) && (v.totals?.customer_pending || 0) > 0)
    .map((v) => ({
      id: v.id,
      customer: v.customer_name || "—",
      mobile: v.customer_mobile || "",
      model: v.model,
      reg_no: v.reg_no,
      pending: v.totals.customer_pending,
    }));

  const cards = [
    { label: "Total Yard Stock Value", value: val.yard_stock, icon: Boxes, color: "#1E3A8A",
      subtitle: "(Includes Available + Booked showroom inventory at purchase cost)" },
    { label: "What We Owe (Payables)", value: val.we_owe, icon: TrendingDown, color: "#EA580C", modal: "payables" },
    { label: "Owed To Us (Receivables)", value: val.owed_to_us, icon: TrendingUp, color: "#16A34A", modal: "receivables" },
    { label: "Cash In Hand", value: val.cash_in_hand, icon: Wallet, color: val.cash_in_hand < 0 ? "#DC2626" : "#0F172A" },
    { label: "Bank Balance", value: val.bank_balance, icon: Landmark, color: val.bank_balance < 0 ? "#DC2626" : "#2563EB" },
    { label: "Total Net Worth", value: val.net_worth, icon: CheckCircle2, color: val.net_worth < 0 ? "#DC2626" : "#1E3A8A",
      subtitle: "(Yard Stock + Cash in Hand + Bank Balance + Receivables − Payables)" },
  ];

  const rangeLabel = ranges.find((r) => r.key === range)?.label || "";

  return (
    <div className="a1-fade">
      <div className="no-print">
        <h1 className="font-head font-black text-3xl sm:text-4xl tracking-tight mb-1">Financial Audit Sheet</h1>
        <p className="text-slate-500 font-semibold mb-6">Owner-only live valuation, performance reports &amp; gap monitor.</p>

        {/* Gap alert */}
        <div
          className="rounded-2xl p-6 mb-6 flex items-center gap-4"
          style={{ background: val.alert ? "#FEF2F2" : "#F0FDF4", border: `2px solid ${val.alert ? "#FCA5A5" : "#86EFAC"}` }}
          data-testid="audit-alert"
        >
          {val.alert ? <AlertTriangle className="w-10 h-10 text-red-500 shrink-0" /> : <CheckCircle2 className="w-10 h-10 text-green-600 shrink-0" />}
          <div>
            <div className="font-head font-extrabold text-xl tracking-tight" style={{ color: val.alert ? "#B91C1C" : "#15803D" }}>
              {val.alert ? "⚠ CASH GAP DETECTED — cash cannot cover payables" : "Healthy — cash covers all payables"}
            </div>
            <div className="font-semibold text-slate-600">
              Coverage gap (Cash + Bank − Payables):{" "}
              <span className="num font-bold" style={{ color: val.coverage_gap < 0 ? "#B91C1C" : "#15803D" }}>{formatINR(val.coverage_gap)}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 mb-8">
          {cards.map((c) => {
            const inner = (
              <>
                <c.icon className="w-8 h-8 mb-3" style={{ color: c.color }} strokeWidth={2.5} />
                <div className="num font-head font-black text-3xl tracking-tight" style={{ color: c.color }}>{formatINR(c.value)}</div>
                <div className="text-sm font-bold text-slate-500 mt-1">{c.label}</div>
                {c.subtitle && <div className="text-[11px] font-semibold text-slate-400 mt-1 leading-tight">{c.subtitle}</div>}
                {c.modal && <div className="text-[11px] font-bold text-[#1E3A8A] mt-2">Tap to view itemized list →</div>}
              </>
            );
            const testid = `audit-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`;
            return c.modal ? (
              <button key={c.label} onClick={() => setModal(c.modal)} data-testid={testid}
                className="a1-card p-6 text-left hover:border-slate-300 transition-colors">
                {inner}
              </button>
            ) : (
              <div key={c.label} className="a1-card p-6" data-testid={testid}>{inner}</div>
            );
          })}
        </div>

        {/* Range selector */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="font-head font-extrabold text-2xl tracking-tight">Performance Report</h2>
          <button data-testid="print-mtd" onClick={() => printSection("report")} className="a1-btn a1-btn-action !h-12 !text-base">
            <Printer className="w-5 h-5" /> Print {rangeLabel} Report
          </button>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 mb-5">
          {ranges.map((r) => (
            <button
              key={r.key}
              data-testid={`range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`px-5 py-2.5 rounded-xl font-bold whitespace-nowrap transition-colors ${
                range === r.key ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        {range === "custom" && (
          <div className="flex gap-4 flex-wrap mb-6 items-end">
            <div>
              <label className="a1-label">From</label>
              <input data-testid="custom-start" type="date" className="a1-input" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div>
              <label className="a1-label">To</label>
              <input data-testid="custom-end" type="date" className="a1-input" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
          </div>
        )}
      </div>

      {/* Printable report region */}
      <div id="print-report">
        {report && <ReportBody report={report} rangeLabel={rangeLabel} val={val} />}
      </div>

      {modal === "payables" && (
        <BreakdownModal
          title="What We Owe — Payables"
          subtitle="Pending mediator held balances & direct seller dues"
          total={val.we_owe}
          columns={["Party", "Vehicle", "Type", "Balance Owed"]}
          rows={payableItems.map((x) => [x.party, `${x.model} (${x.reg_no})`, x.channel, formatINR(x.held)])}
          totalColor="#EA580C"
          printName="Print Payables List"
          onClose={() => setModal(null)}
        />
      )}
      {modal === "receivables" && (
        <BreakdownModal
          title="Owed To Us — Receivables"
          subtitle="Customer pending balances on booked & sold cars"
          total={val.owed_to_us}
          columns={["Customer", "Vehicle", "Mobile", "Balance Due"]}
          rows={receivableItems.map((x) => [x.customer, `${x.model} (${x.reg_no})`, x.mobile || "—", formatINR(x.pending)])}
          totalColor="#16A34A"
          printName="Print Receivables List"
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

function BreakdownModal({ title, subtitle, total, columns, rows, totalColor, printName, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "24px 12px", overflowY: "auto" }}>
      <div className="no-print" onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,.5)" }} />
      <div className="a1-fade" style={{ position: "relative", background: "#fff", borderRadius: 16, width: "100%", maxWidth: 720, boxShadow: "0 20px 60px rgba(0,0,0,.3)" }}>
        <div className="no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: "1px solid #E2E8F0" }}>
          <span className="font-head font-extrabold text-lg tracking-tight">{title}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button data-testid="breakdown-print" onClick={() => printSection("list")} className="a1-btn a1-btn-action !h-10 !text-sm !px-4">
              <Printer className="w-4 h-4" /> {printName}
            </button>
            <button data-testid="breakdown-close" onClick={onClose} className="a1-btn a1-btn-ghost !h-10 !text-sm !px-3">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div id="print-list" style={{ padding: "20px 24px" }}>
          <div className="hidden print:block" style={{ borderBottom: "3px solid #1E3A8A", paddingBottom: 10, marginBottom: 12 }}>
            <div className="font-head font-black text-2xl" style={{ color: "#1E3A8A" }}>A1 CARS AGENCY</div>
            <div className="text-sm font-bold text-slate-500">{title} • {formatDate(new Date())}</div>
          </div>
          <p className="text-sm font-semibold text-slate-500 mb-3">{subtitle}</p>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#1E3A8A] text-white text-left">
                {columns.map((c, i) => (
                  <th key={c} className={`p-3 font-bold ${i === columns.length - 1 ? "text-right" : ""}`}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td className="p-4 text-slate-400 font-semibold" colSpan={columns.length}>Nothing pending. All clear.</td></tr>
              )}
              {rows.map((r, ri) => (
                <tr key={ri} className="border-t border-slate-200">
                  {r.map((cell, ci) => (
                    <td key={ci} className={`p-3 font-semibold ${ci === r.length - 1 ? "num font-bold text-right" : ""} ${ci === 2 && columns[2] === "Type" ? "uppercase text-xs text-slate-500" : ""}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: "2px solid #0F172A" }}>
                <td className="p-3 font-black" colSpan={columns.length - 1}>TOTAL</td>
                <td className="p-3 num font-head font-black text-right text-lg" style={{ color: totalColor }}>{formatINR(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, icon: Icon, color, prefixCount }) {
  return (
    <div className="a1-card p-5" data-testid={`report-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-5 h-5" style={{ color }} />}
        <span className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</span>
      </div>
      <div className="num font-head font-black text-2xl tracking-tight" style={{ color: color || "#0F172A" }}>
        {prefixCount ? value : formatINR(value)}
      </div>
    </div>
  );
}

function ReportBody({ report, rangeLabel, val }) {
  return (
    <div style={{ position: "relative" }}>
      <div className="hidden print:block" style={{ marginBottom: 16 }}>
        <div className="flex items-center gap-3" style={{ borderBottom: "3px solid #1E3A8A", paddingBottom: 12 }}>
          <img src="/logo.png" alt="A1" style={{ width: 56, height: 56, borderRadius: "50%" }} />
          <div>
            <div className="font-head font-black text-2xl" style={{ color: "#1E3A8A" }}>A1 CARS AGENCY</div>
            <div className="text-sm font-bold text-slate-500">Performance Report • {rangeLabel} ({formatDate(report.start)} – {formatDate(report.end)})</div>
          </div>
        </div>
      </div>

      {report.alert && (
        <div className="rounded-xl p-4 mb-5 font-bold" style={{ background: "#FEF2F2", border: "2px solid #FCA5A5", color: "#B91C1C" }}>
          ⚠ BALANCE GAP DETECTED — coverage gap {formatINR(val.coverage_gap)}. Verify cash drawer &amp; payables.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
        <Metric label="Vehicles Inward" value={report.inward} icon={PackagePlus} color="#1E3A8A" prefixCount />
        <Metric label="Vehicles Delivered" value={report.delivered} icon={Truck} color="#475569" prefixCount />
        <Metric label="Sales Revenue" value={report.revenue} icon={TrendingUp} color="#16A34A" />
        <Metric label="Purchase Cost" value={report.purchase_cost} icon={TrendingDown} color="#EA580C" />
        <Metric label="Net Profit Margin" value={report.net_profit} icon={CheckCircle2} color={report.net_profit >= 0 ? "#16A34A" : "#DC2626"} />
        <Metric label="Cash Inflow" value={report.cash_in} icon={Wallet} color="#0F172A" />
        <Metric label="Bank / Online Inflow" value={report.bank_in} icon={Landmark} color="#2563EB" />
        <Metric label="Total Outflows" value={report.outflows} icon={ArrowDownRight} color="#EA580C" />
      </div>

      <h3 className="font-head font-extrabold text-xl tracking-tight mb-3">Ledger — {rangeLabel}</h3>
      <div className="a1-card overflow-hidden" data-testid="report-ledger">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#1E3A8A] text-white text-left">
              <th className="p-3 font-bold">Date</th>
              <th className="p-3 font-bold">Narration</th>
              <th className="p-3 font-bold">Type</th>
              <th className="p-3 font-bold text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {report.ledger.length === 0 && (
              <tr><td className="p-4 text-slate-400 font-semibold" colSpan={4}>No transactions in this period.</td></tr>
            )}
            {report.ledger.map((t) => (
              <tr key={t.id} className="border-t border-slate-200">
                <td className="p-3 num font-semibold whitespace-nowrap">{formatDate(t.date)}</td>
                <td className="p-3 font-semibold">{t.narration}</td>
                <td className="p-3 font-bold uppercase text-xs text-slate-500">{t.kind} {t.utr ? `• ${t.utr}` : ""}</td>
                <td className="p-3 num font-bold text-right" style={{ color: t.direction === "in" ? "#16A34A" : "#EA580C" }}>
                  {t.direction === "in" ? "+" : "-"}{formatINR(t.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
